import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  IndianRupee, TrendingUp, TrendingDown, AlertTriangle, Users, FileText,
  Plus, Edit2, Eye, Download, Search, Filter, RefreshCw, Building2,
  Calendar, CreditCard, Clock, CheckCircle, XCircle, ArrowUpRight,
  PieChart, BarChart3, Receipt, Banknote, AlertCircle, Trash2, Calculator,
  Upload, FileUp, Loader2, Check, X, Sparkles, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell } from 'recharts';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'];

const AccountsReceivable = ({ user, setUser }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // Clients
  const [clients, setClients] = useState([]);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({
    name: '', gst_number: '', pan: '', tan: '', address: '', city: '', state: '', pincode: '',
    credit_limit: '', payment_terms_days: '30', contact_person: '', contact_email: '', contact_phone: '', notes: '',
    tds_applicable: false, tds_rate: '10', tds_section: '194C'
  });
  
  // TDS Configuration Dialog
  const [tdsConfigDialogOpen, setTdsConfigDialogOpen] = useState(false);
  const [selectedClientForTds, setSelectedClientForTds] = useState(null);
  const [tdsConfigForm, setTdsConfigForm] = useState({
    tds_applicable: false,
    tds_rate: '10',
    tds_section: '194C',
    tan: ''
  });
  
  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [invoiceForm, setInvoiceForm] = useState({
    client_id: '', service_type_id: '', invoice_date: new Date().toISOString().split('T')[0],
    due_date: '', project_name: '', location: '', description: '',
    line_items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
    gst_rate: 18, is_same_state: true, tds_applicable: false, tds_rate: 0, notes: ''
  });
  
  // Payments
  const [payments, setPayments] = useState([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '', tds_deducted: '0', payment_mode: 'NEFT', bank_reference: '', notes: ''
  });
  
  // Aging report
  const [agingData, setAgingData] = useState([]);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'invoice_date', direction: 'desc' });

  // Bulk Upload States
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extractedInvoices, setExtractedInvoices] = useState([]);
  const [savingInvoices, setSavingInvoices] = useState(false);
  const fileInputRef = useRef(null);

  // GST Consolidated View and Client Summary
  const [gstConsolidatedData, setGstConsolidatedData] = useState([]);
  const [clientSummaryData, setClientSummaryData] = useState([]);
  const [expandedGstGroups, setExpandedGstGroups] = useState({});
  const [clientsViewMode, setClientsViewMode] = useState('list'); // 'list', 'gst', 'summary'

  // Reports State
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDateRange, setReportDateRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchDashboard(),
        fetchClients(),
        fetchInvoices(),
        fetchPayments(),
        fetchServiceTypes(),
        fetchAgingReport(),
        fetchGstConsolidated(),
        fetchClientSummary()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/receivables/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Dashboard error:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/receivables/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Clients error:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await api.get('/receivables/invoices');
      setInvoices(response.data);
    } catch (error) {
      console.error('Invoices error:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await api.get('/receivables/payments');
      setPayments(response.data);
    } catch (error) {
      console.error('Payments error:', error);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const response = await api.get('/receivables/service-types');
      setServiceTypes(response.data);
    } catch (error) {
      console.error('Service types error:', error);
    }
  };

  const fetchAgingReport = async () => {
    try {
      const response = await api.get('/receivables/aging-report');
      setAgingData(response.data);
    } catch (error) {
      console.error('Aging error:', error);
    }
  };

  const fetchGstConsolidated = async () => {
    try {
      const response = await api.get('/receivables/gst-consolidated');
      setGstConsolidatedData(response.data);
    } catch (error) {
      console.error('GST consolidated error:', error);
    }
  };

  const fetchClientSummary = async () => {
    try {
      const response = await api.get('/receivables/client-summary');
      setClientSummaryData(response.data);
    } catch (error) {
      console.error('Client summary error:', error);
    }
  };

  // Report Functions
  const generateReport = async (reportType) => {
    setSelectedReport(reportType);
    setReportLoading(true);
    setReportData(null);
    
    try {
      let data = null;
      
      switch (reportType) {
        case 'Outstanding Summary':
          // Generate outstanding summary from clients and invoices
          const outstandingByClient = clients.map(client => {
            const clientInvoices = invoices.filter(inv => inv.client_id === client.id);
            const totalInvoiced = clientInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
            const totalReceived = clientInvoices.reduce((sum, inv) => sum + (inv.amount_received || 0), 0);
            const totalOutstanding = clientInvoices.reduce((sum, inv) => sum + (inv.amount_due || 0), 0);
            const overdueAmount = clientInvoices
              .filter(inv => new Date(inv.due_date) < new Date() && inv.status !== 'Paid')
              .reduce((sum, inv) => sum + (inv.amount_due || 0), 0);
            const invoiceCount = clientInvoices.length;
            const pendingCount = clientInvoices.filter(inv => inv.status !== 'Paid').length;
            
            return {
              client_name: client.name,
              gst_number: client.gst_number,
              invoice_count: invoiceCount,
              pending_count: pendingCount,
              total_invoiced: totalInvoiced,
              total_received: totalReceived,
              total_outstanding: totalOutstanding,
              overdue_amount: overdueAmount,
              credit_limit: client.credit_limit || 0,
              available_credit: (client.credit_limit || 0) - totalOutstanding
            };
          }).filter(c => c.invoice_count > 0).sort((a, b) => b.total_outstanding - a.total_outstanding);
          
          const totalOutstanding = outstandingByClient.reduce((sum, c) => sum + c.total_outstanding, 0);
          const totalOverdue = outstandingByClient.reduce((sum, c) => sum + c.overdue_amount, 0);
          
          data = {
            summary: {
              total_clients: outstandingByClient.length,
              total_outstanding: totalOutstanding,
              total_overdue: totalOverdue,
              overdue_percentage: totalOutstanding > 0 ? ((totalOverdue / totalOutstanding) * 100).toFixed(1) : 0
            },
            details: outstandingByClient
          };
          break;
          
        case 'GST Summary':
          // Generate GST summary from invoices
          const gstInvoices = invoices.filter(inv => {
            const invDate = new Date(inv.invoice_date);
            const from = new Date(reportDateRange.from);
            const to = new Date(reportDateRange.to);
            return invDate >= from && invDate <= to;
          });
          
          const gstByRate = {};
          gstInvoices.forEach(inv => {
            const rate = inv.gst_rate || 18;
            const key = `${rate}%`;
            if (!gstByRate[key]) {
              gstByRate[key] = { rate, taxable_value: 0, cgst: 0, sgst: 0, igst: 0, total_gst: 0, invoice_count: 0 };
            }
            const taxableValue = inv.subtotal || 0;
            // Use actual CGST, SGST, IGST values from invoice
            const cgstAmount = inv.cgst || 0;
            const sgstAmount = inv.sgst || 0;
            const igstAmount = inv.igst || 0;
            const totalGstAmount = cgstAmount + sgstAmount + igstAmount;
            
            gstByRate[key].taxable_value += taxableValue;
            gstByRate[key].cgst += cgstAmount;
            gstByRate[key].sgst += sgstAmount;
            gstByRate[key].igst += igstAmount;
            gstByRate[key].total_gst += totalGstAmount;
            gstByRate[key].invoice_count += 1;
          });
          
          const gstDetails = Object.values(gstByRate).sort((a, b) => b.rate - a.rate);
          const totalTaxable = gstDetails.reduce((sum, g) => sum + g.taxable_value, 0);
          const totalGst = gstDetails.reduce((sum, g) => sum + g.total_gst, 0);
          
          data = {
            period: { from: reportDateRange.from, to: reportDateRange.to },
            summary: {
              total_invoices: gstInvoices.length,
              total_taxable_value: totalTaxable,
              total_cgst: gstDetails.reduce((sum, g) => sum + g.cgst, 0),
              total_sgst: gstDetails.reduce((sum, g) => sum + g.sgst, 0),
              total_igst: gstDetails.reduce((sum, g) => sum + g.igst, 0),
              total_gst: totalGst
            },
            details: gstDetails
          };
          break;
          
        case 'TDS Reconciliation':
          // Generate TDS reconciliation from payments and invoices
          const tdsInvoices = invoices.filter(inv => inv.tds_applicable);
          const tdsPayments = payments.filter(p => p.tds_deducted > 0);
          
          const tdsByClient = {};
          tdsPayments.forEach(payment => {
            const invoice = invoices.find(inv => inv.id === payment.invoice_id);
            if (invoice) {
              const client = clients.find(c => c.id === invoice.client_id);
              const clientName = client?.name || 'Unknown';
              if (!tdsByClient[clientName]) {
                tdsByClient[clientName] = { 
                  client_name: clientName,
                  tan: client?.tan || '-',
                  total_amount: 0,
                  tds_rate: invoice.tds_rate || 0,
                  tds_deducted: 0,
                  payments: []
                };
              }
              tdsByClient[clientName].total_amount += payment.amount;
              tdsByClient[clientName].tds_deducted += payment.tds_deducted;
              tdsByClient[clientName].payments.push({
                invoice_number: invoice.invoice_number,
                payment_date: payment.payment_date,
                amount: payment.amount,
                tds_deducted: payment.tds_deducted
              });
            }
          });
          
          const tdsDetails = Object.values(tdsByClient).sort((a, b) => b.tds_deducted - a.tds_deducted);
          
          data = {
            summary: {
              total_tds_applicable_invoices: tdsInvoices.length,
              total_tds_deducted: tdsDetails.reduce((sum, t) => sum + t.tds_deducted, 0),
              clients_with_tds: tdsDetails.length
            },
            details: tdsDetails
          };
          break;
          
        case 'Collection Report':
          // Generate collection efficiency report
          const periodInvoices = invoices.filter(inv => {
            const invDate = new Date(inv.invoice_date);
            const from = new Date(reportDateRange.from);
            const to = new Date(reportDateRange.to);
            return invDate >= from && invDate <= to;
          });
          
          const periodPayments = payments.filter(p => {
            const payDate = new Date(p.payment_date);
            const from = new Date(reportDateRange.from);
            const to = new Date(reportDateRange.to);
            return payDate >= from && payDate <= to;
          });
          
          const totalInvoiced = periodInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
          const totalCollected = periodPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
          const collectionEfficiency = totalInvoiced > 0 ? ((totalCollected / totalInvoiced) * 100).toFixed(1) : 0;
          
          // Monthly breakdown
          const monthlyData = {};
          periodInvoices.forEach(inv => {
            const month = new Date(inv.invoice_date).toLocaleString('default', { month: 'short', year: 'numeric' });
            if (!monthlyData[month]) {
              monthlyData[month] = { month, invoiced: 0, collected: 0, pending: 0 };
            }
            monthlyData[month].invoiced += inv.total_amount || 0;
          });
          
          periodPayments.forEach(p => {
            const invoice = invoices.find(inv => inv.id === p.invoice_id);
            if (invoice) {
              const month = new Date(p.payment_date).toLocaleString('default', { month: 'short', year: 'numeric' });
              if (!monthlyData[month]) {
                monthlyData[month] = { month, invoiced: 0, collected: 0, pending: 0 };
              }
              monthlyData[month].collected += p.amount || 0;
            }
          });
          
          Object.values(monthlyData).forEach(m => {
            m.pending = m.invoiced - m.collected;
            m.efficiency = m.invoiced > 0 ? ((m.collected / m.invoiced) * 100).toFixed(1) : 0;
          });
          
          // Payment mode breakdown
          const paymentModes = {};
          periodPayments.forEach(p => {
            const mode = p.payment_mode || 'Other';
            if (!paymentModes[mode]) {
              paymentModes[mode] = { mode, count: 0, amount: 0 };
            }
            paymentModes[mode].count += 1;
            paymentModes[mode].amount += p.amount || 0;
          });
          
          data = {
            period: { from: reportDateRange.from, to: reportDateRange.to },
            summary: {
              total_invoiced: totalInvoiced,
              total_collected: totalCollected,
              total_pending: totalInvoiced - totalCollected,
              collection_efficiency: collectionEfficiency,
              invoice_count: periodInvoices.length,
              payment_count: periodPayments.length
            },
            monthly: Object.values(monthlyData).sort((a, b) => new Date(a.month) - new Date(b.month)),
            payment_modes: Object.values(paymentModes).sort((a, b) => b.amount - a.amount)
          };
          break;
          
        default:
          data = { message: 'Report not implemented' };
      }
      
      setReportData(data);
    } catch (error) {
      console.error('Report generation error:', error);
      toast.error('Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  const exportReportToCsv = () => {
    if (!reportData || !selectedReport) return;
    
    let csvContent = '';
    const fileName = `${selectedReport.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    
    switch (selectedReport) {
      case 'Outstanding Summary':
        csvContent = 'Client Name,GST Number,Invoice Count,Pending Count,Total Invoiced,Total Received,Outstanding,Overdue\n';
        reportData.details.forEach(row => {
          csvContent += `"${row.client_name}","${row.gst_number}",${row.invoice_count},${row.pending_count},${row.total_invoiced},${row.total_received},${row.total_outstanding},${row.overdue_amount}\n`;
        });
        break;
      case 'GST Summary':
        csvContent = 'GST Rate,Invoice Count,Taxable Value,CGST,SGST,IGST,Total GST\n';
        reportData.details.forEach(row => {
          csvContent += `${row.rate}%,${row.invoice_count},${row.taxable_value},${row.cgst},${row.sgst},${row.igst},${row.total_gst}\n`;
        });
        break;
      case 'TDS Reconciliation':
        csvContent = 'Client Name,TAN,Total Amount,TDS Rate,TDS Deducted\n';
        reportData.details.forEach(row => {
          csvContent += `"${row.client_name}","${row.tan}",${row.total_amount},${row.tds_rate}%,${row.tds_deducted}\n`;
        });
        break;
      case 'Collection Report':
        csvContent = 'Month,Invoiced,Collected,Pending,Efficiency %\n';
        reportData.monthly?.forEach(row => {
          csvContent += `${row.month},${row.invoiced},${row.collected},${row.pending},${row.efficiency}%\n`;
        });
        break;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    toast.success('Report exported successfully');
  };

  // TDS Configuration Functions
  const openTdsConfigDialog = (client) => {
    setSelectedClientForTds(client);
    setTdsConfigForm({
      tds_applicable: client.tds_applicable || false,
      tds_rate: client.tds_rate?.toString() || '10',
      tds_section: client.tds_section || '194C',
      tan: client.tan || ''
    });
    setTdsConfigDialogOpen(true);
  };

  const handleSaveTdsConfig = async () => {
    if (!selectedClientForTds) return;
    
    try {
      const updateData = {
        ...selectedClientForTds,
        tds_applicable: tdsConfigForm.tds_applicable,
        tds_rate: parseFloat(tdsConfigForm.tds_rate) || 0,
        tds_section: tdsConfigForm.tds_section,
        tan: tdsConfigForm.tan
      };
      
      await api.put(`/receivables/clients/${selectedClientForTds.id}`, updateData);
      toast.success('TDS configuration saved successfully');
      setTdsConfigDialogOpen(false);
      fetchClients();
    } catch (error) {
      toast.error('Failed to save TDS configuration');
    }
  };

  // Client handlers
  const handleSaveClient = async () => {
    try {
      if (editingClient) {
        await api.put(`/receivables/clients/${editingClient.id}`, clientForm);
        toast.success('Client updated successfully');
      } else {
        await api.post('/receivables/clients', clientForm);
        toast.success('Client created successfully');
      }
      setClientDialogOpen(false);
      resetClientForm();
      fetchClients();
      fetchGstConsolidated();
      fetchClientSummary();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save client';
      toast.error(errorMessage);
    }
  };

  const resetClientForm = () => {
    setEditingClient(null);
    setClientForm({
      name: '', gst_number: '', pan: '', address: '', city: '', state: '', pincode: '',
      credit_limit: '', payment_terms_days: '30', contact_person: '', contact_email: '', contact_phone: '', notes: ''
    });
  };

  const editClient = (client) => {
    setEditingClient(client);
    setClientForm({
      name: client.name || '',
      gst_number: client.gst_number || '',
      pan: client.pan || '',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      pincode: client.pincode || '',
      credit_limit: client.credit_limit?.toString() || '',
      payment_terms_days: client.payment_terms_days?.toString() || '30',
      contact_person: client.contact_person || '',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
      notes: client.notes || ''
    });
    setClientDialogOpen(true);
  };

  const handleDeleteClient = async (client) => {
    if (!window.confirm(`Are you sure you want to delete "${client.name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/receivables/clients/${client.id}`);
      toast.success('Client deleted successfully');
      fetchClients();
      fetchDashboard();
      fetchGstConsolidated();
      fetchClientSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  // Invoice handlers
  const calculateInvoiceTotals = (form) => {
    const subtotal = form.line_items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const gstRate = parseFloat(form.gst_rate) || 0;
    const cgst = form.is_same_state ? subtotal * (gstRate / 2) / 100 : 0;
    const sgst = form.is_same_state ? subtotal * (gstRate / 2) / 100 : 0;
    const igst = !form.is_same_state ? subtotal * gstRate / 100 : 0;
    const totalGst = cgst + sgst + igst;
    const total = subtotal + totalGst;
    return { subtotal, cgst, sgst, igst, totalGst, total };
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...invoiceForm.line_items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].rate) || 0);
    }
    setInvoiceForm({ ...invoiceForm, line_items: newItems });
  };

  const addLineItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      line_items: [...invoiceForm.line_items, { description: '', quantity: 1, rate: 0, amount: 0 }]
    });
  };

  const removeLineItem = (index) => {
    if (invoiceForm.line_items.length > 1) {
      const newItems = invoiceForm.line_items.filter((_, i) => i !== index);
      setInvoiceForm({ ...invoiceForm, line_items: newItems });
    }
  };

  const handleSaveInvoice = async () => {
    try {
      if (!invoiceForm.client_id) {
        toast.error('Please select a client');
        return;
      }
      
      if (editingInvoice) {
        await api.put(`/receivables/invoices/${editingInvoice.id}`, invoiceForm);
        toast.success('Invoice updated successfully');
      } else {
        await api.post('/receivables/invoices', invoiceForm);
        toast.success('Invoice created successfully');
      }
      setInvoiceDialogOpen(false);
      resetInvoiceForm();
      fetchInvoices();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to save invoice');
    }
  };

  const resetInvoiceForm = () => {
    setEditingInvoice(null);
    setInvoiceForm({
      client_id: '', service_type_id: '', invoice_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 45 * 86400000).toISOString().split('T')[0], project_name: '', location: '', description: '',
      line_items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
      gst_rate: 18, is_same_state: true, tds_applicable: false, tds_rate: 0, notes: ''
    });
  };

  const editInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setInvoiceForm({
      client_id: invoice.client_id || '',
      service_type_id: invoice.service_type_id || '',
      invoice_date: invoice.invoice_date || new Date().toISOString().split('T')[0],
      due_date: invoice.due_date || '',
      project_name: invoice.project_name || '',
      location: invoice.location || '',
      description: invoice.description || '',
      line_items: invoice.line_items?.length > 0 ? invoice.line_items : [{ description: '', quantity: 1, rate: 0, amount: 0 }],
      gst_rate: invoice.gst_rate || 18,
      is_same_state: invoice.is_same_state !== false,
      tds_applicable: invoice.tds_applicable || false,
      tds_rate: invoice.tds_rate || 0,
      notes: invoice.notes || ''
    });
    setInvoiceDialogOpen(true);
  };

  // Payment handlers
  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: invoice.amount_due?.toString() || '',
      tds_deducted: '0',
      payment_mode: 'NEFT',
      bank_reference: '',
      notes: ''
    });
    setPaymentDialogOpen(true);
  };

  const handleSavePayment = async () => {
    try {
      if (!selectedInvoice) return;
      
      await api.post('/receivables/payments', {
        invoice_id: selectedInvoice.id,
        ...paymentForm
      });
      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      setSelectedInvoice(null);
      fetchInvoices();
      fetchPayments();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  // Bulk Upload Handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFiles.length !== files.length) {
      toast.warning('Only PDF files are supported. Non-PDF files were ignored.');
    }
    setUploadedFiles(pdfFiles);
    setExtractedInvoices([]);
  };

  const handleExtractInvoices = async () => {
    if (uploadedFiles.length === 0) {
      toast.error('Please select PDF files first');
      return;
    }

    setExtracting(true);
    const results = [];

    for (const file of uploadedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/receivables/extract-invoice', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.success) {
          results.push({
            filename: file.name,
            success: true,
            data: response.data.extracted_data,
            existingClient: response.data.existing_client,
            selected: true,
            createNewClient: !response.data.existing_client
          });
        } else {
          results.push({
            filename: file.name,
            success: false,
            error: response.data.error
          });
        }
      } catch (error) {
        results.push({
          filename: file.name,
          success: false,
          error: error.response?.data?.detail || error.message
        });
      }
    }

    setExtractedInvoices(results);
    setExtracting(false);

    const successCount = results.filter(r => r.success).length;
    if (successCount > 0) {
      toast.success(`Extracted ${successCount} invoice(s) successfully`);
    }
    if (results.length - successCount > 0) {
      toast.error(`Failed to extract ${results.length - successCount} invoice(s)`);
    }
  };

  const updateExtractedInvoice = (index, field, value) => {
    const updated = [...extractedInvoices];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updated[index].data[parent][child] = value;
    } else if (field === 'selected' || field === 'createNewClient' || field === 'client_id') {
      updated[index][field] = value;
    } else {
      updated[index].data[field] = value;
    }
    setExtractedInvoices(updated);
  };

  const handleSaveExtractedInvoices = async (overwriteDuplicates = false) => {
    const selectedInvoices = extractedInvoices.filter(inv => inv.success && inv.selected);
    if (selectedInvoices.length === 0) {
      toast.error('No invoices selected to save');
      return;
    }

    setSavingInvoices(true);

    try {
      const invoicesToSave = selectedInvoices.map(inv => ({
        ...inv.data,
        client_id: inv.client_id || inv.existingClient?.id,
        create_new_client: inv.createNewClient,
        client_name: inv.data.client_name,
        client_gst: inv.data.client_gst,
        client_pan: inv.data.client_pan,
        client_address: inv.data.client_address,
        client_city: inv.data.client_city,
        client_state: inv.data.client_state,
        filename: inv.filename
      }));

      const response = await api.post('/receivables/save-extracted-invoices', {
        invoices: invoicesToSave,
        overwrite_duplicates: overwriteDuplicates
      });

      if (response.data.saved_count > 0) {
        toast.success(`Saved ${response.data.saved_count} invoice(s) successfully`);
      }

      if (response.data.skipped_duplicates > 0) {
        const confirmOverwrite = window.confirm(
          `${response.data.skipped_duplicates} duplicate invoice(s) found. Do you want to overwrite them?`
        );
        if (confirmOverwrite) {
          // Retry with overwrite flag
          await handleSaveExtractedInvoices(true);
          return;
        } else {
          toast.warning(`Skipped ${response.data.skipped_duplicates} duplicate invoice(s)`);
        }
      }

      if (response.data.error_count > 0) {
        const errorDetails = response.data.errors?.map(e => `${e.invoice}: ${e.error}`).join(', ');
        toast.error(`Failed to save ${response.data.error_count} invoice(s): ${errorDetails || 'Unknown error'}`);
        console.error('Invoice save errors:', response.data.errors);
      }

      // Close dialog and refresh if any were saved
      if (response.data.saved_count > 0 || response.data.skipped_duplicates > 0) {
        setBulkUploadDialogOpen(false);
        setUploadedFiles([]);
        setExtractedInvoices([]);
        fetchInvoices();
        fetchClients();
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to save invoices');
    } finally {
      setSavingInvoices(false);
    }
  };

  const resetBulkUpload = () => {
    setUploadedFiles([]);
    setExtractedInvoices([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Delete Invoice Handler
  const handleDeleteInvoice = async (invoiceId, invoiceNumber) => {
    if (!window.confirm(`Are you sure you want to delete invoice ${invoiceNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/receivables/invoices/${invoiceId}`);
      toast.success(`Invoice ${invoiceNumber} deleted successfully`);
      fetchInvoices();
      fetchDashboard();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to delete invoice');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      partially_paid: 'bg-blue-100 text-blue-700',
      overdue: 'bg-red-100 text-red-700',
      closed: 'bg-emerald-100 text-emerald-700'
    };
    return <Badge className={styles[status] || 'bg-slate-100 text-slate-700'}>{status?.replace('_', ' ')}</Badge>;
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortHeader = ({ field, children, className = '' }) => (
    <TableHead className={`cursor-pointer select-none hover:bg-slate-50 ${className}`} onClick={() => handleSort(field)}>
      <span className="flex items-center gap-1">
        {children}
        {sortConfig.key === field ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}
      </span>
    </TableHead>
  );

  const sortData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (typeof aVal === 'string') aVal = aVal?.toLowerCase() || '';
      if (typeof bVal === 'string') bVal = bVal?.toLowerCase() || '';
      if (typeof aVal === 'number' || !isNaN(Number(aVal))) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filterStatus !== 'all' && inv.status !== filterStatus) return false;
    if (filterClient !== 'all' && inv.client_id !== filterClient) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return inv.invoice_number?.toLowerCase().includes(query) ||
        inv.client_name?.toLowerCase().includes(query);
    }
    return true;
  });

  const agingChartData = dashboardData ? [
    { name: '0-30 Days', value: dashboardData.aging?.['0_30'] || 0 },
    { name: '31-60 Days', value: dashboardData.aging?.['31_60'] || 0 },
    { name: '61-90 Days', value: dashboardData.aging?.['61_90'] || 0 },
    { name: '90+ Days', value: dashboardData.aging?.['90_plus'] || 0 }
  ] : [];

  if (loading && !dashboardData) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="accounts-receivable">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Accounts Receivable</h1>
            <p className="text-slate-500 text-sm mt-1">Manage invoices, track payments, and monitor receivables</p>
          </div>
          <Button onClick={fetchAllData} variant="outline" className="gap-2">
            <RefreshCw size={16} />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-7 lg:w-auto lg:inline-flex">
            <TabsTrigger value="dashboard" className="gap-2"><PieChart size={16} /> Dashboard</TabsTrigger>
            <TabsTrigger value="clients" className="gap-2"><Users size={16} /> Clients</TabsTrigger>
            <TabsTrigger value="invoices" className="gap-2"><FileText size={16} /> Invoices</TabsTrigger>
            <TabsTrigger value="bulk-upload" className="gap-2"><FileUp size={16} /> Bulk Upload</TabsTrigger>
            <TabsTrigger value="payments" className="gap-2"><Banknote size={16} /> Payments</TabsTrigger>
            <TabsTrigger value="aging" className="gap-2"><Clock size={16} /> Aging</TabsTrigger>
            <TabsTrigger value="reports" className="gap-2"><BarChart3 size={16} /> Reports</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Total Receivable</p>
                      <p className="text-2xl font-bold text-blue-900">{formatCurrency(dashboardData?.total_receivable)}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                      <IndianRupee className="text-white" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-600 font-medium">Overdue Amount</p>
                      <p className="text-2xl font-bold text-red-900">{formatCurrency(dashboardData?.overdue_amount)}</p>
                    </div>
                    <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                      <AlertTriangle className="text-white" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-600 font-medium">This Month Collection</p>
                      <p className="text-2xl font-bold text-emerald-900">{formatCurrency(dashboardData?.this_month_collection)}</p>
                    </div>
                    <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                      <TrendingUp className="text-white" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Pending Invoices</p>
                      <p className="text-2xl font-bold text-purple-900">{dashboardData?.pending_invoices || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                      <FileText className="text-white" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Aging Pie Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Aging Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <RePieChart>
                        <Pie
                          data={agingChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value }) => value > 0 ? `${name}: ${formatCurrency(value)}` : ''}
                        >
                          {agingChartData.map((entry, index) => (
                            <Cell key={index} fill={COLORS[index]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </RePieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Collection Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Collection Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dashboardData?.collection_trend || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="amount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Top Overdue Clients */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="text-red-500" size={20} />
                  Top Overdue Clients
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-right">Overdue Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboardData?.top_overdue_clients?.map((client, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{client.client_name}</TableCell>
                        <TableCell className="text-right text-red-600 font-semibold">
                          {formatCurrency(client.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!dashboardData?.top_overdue_clients || dashboardData.top_overdue_clients.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center text-slate-500 py-8">
                          No overdue clients
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div className="flex gap-4 items-center">
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    placeholder="Search clients..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                {/* View Mode Selector */}
                <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
                  <Button
                    variant={clientsViewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setClientsViewMode('list')}
                    className={clientsViewMode === 'list' ? 'bg-white shadow-sm' : ''}
                  >
                    <Users size={16} className="mr-1" /> List
                  </Button>
                  <Button
                    variant={clientsViewMode === 'gst' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setClientsViewMode('gst')}
                    className={clientsViewMode === 'gst' ? 'bg-white shadow-sm' : ''}
                  >
                    <Building2 size={16} className="mr-1" /> GST View
                  </Button>
                  <Button
                    variant={clientsViewMode === 'summary' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setClientsViewMode('summary')}
                    className={clientsViewMode === 'summary' ? 'bg-white shadow-sm' : ''}
                  >
                    <BarChart3 size={16} className="mr-1" /> Summary
                  </Button>
                </div>
              </div>
              <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-indigo-600 hover:bg-indigo-700" onClick={resetClientForm}>
                    <Plus size={16} /> Add Client
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-4 py-4">
                    <div className="col-span-2">
                      <Label>Client Name *</Label>
                      <Input
                        value={clientForm.name}
                        onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                        placeholder="Enter client name"
                      />
                    </div>
                    <div>
                      <Label>GST Number</Label>
                      <Input
                        value={clientForm.gst_number}
                        onChange={(e) => setClientForm({ ...clientForm, gst_number: e.target.value.toUpperCase() })}
                        placeholder="22AAAAA0000A1Z5"
                      />
                    </div>
                    <div>
                      <Label>PAN</Label>
                      <Input
                        value={clientForm.pan}
                        onChange={(e) => setClientForm({ ...clientForm, pan: e.target.value.toUpperCase() })}
                        placeholder="AAAAA0000A"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Address</Label>
                      <Textarea
                        value={clientForm.address}
                        onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })}
                        placeholder="Street address"
                      />
                    </div>
                    <div>
                      <Label>City</Label>
                      <Input
                        value={clientForm.city}
                        onChange={(e) => setClientForm({ ...clientForm, city: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input
                        value={clientForm.state}
                        onChange={(e) => setClientForm({ ...clientForm, state: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Pincode</Label>
                      <Input
                        value={clientForm.pincode}
                        onChange={(e) => setClientForm({ ...clientForm, pincode: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Credit Limit (₹)</Label>
                      <Input
                        type="number"
                        value={clientForm.credit_limit}
                        onChange={(e) => setClientForm({ ...clientForm, credit_limit: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Payment Terms (Days)</Label>
                      <Select
                        value={clientForm.payment_terms_days}
                        onValueChange={(v) => setClientForm({ ...clientForm, payment_terms_days: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 Days</SelectItem>
                          <SelectItem value="30">30 Days</SelectItem>
                          <SelectItem value="45">45 Days</SelectItem>
                          <SelectItem value="60">60 Days</SelectItem>
                          <SelectItem value="90">90 Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Contact Person</Label>
                      <Input
                        value={clientForm.contact_person}
                        onChange={(e) => setClientForm({ ...clientForm, contact_person: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Contact Email</Label>
                      <Input
                        type="email"
                        value={clientForm.contact_email}
                        onChange={(e) => setClientForm({ ...clientForm, contact_email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Contact Phone</Label>
                      <Input
                        value={clientForm.contact_phone}
                        onChange={(e) => setClientForm({ ...clientForm, contact_phone: e.target.value })}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={clientForm.notes}
                        onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setClientDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveClient} className="bg-indigo-600 hover:bg-indigo-700">
                      {editingClient ? 'Update' : 'Create'} Client
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* List View */}
            {clientsViewMode === 'list' && (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client Name</TableHead>
                        <TableHead>GST Number</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Payment Terms</TableHead>
                        <TableHead>Credit Limit</TableHead>
                        <TableHead>TDS</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clients
                        .filter(c => !searchQuery || 
                          c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          c.gst_number?.toLowerCase().includes(searchQuery.toLowerCase())
                        )
                        .map((client) => (
                        <TableRow key={client.id}>
                          <TableCell className="font-medium">{client.name}</TableCell>
                          <TableCell className="font-mono text-sm">{client.gst_number || '-'}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p>{client.contact_person}</p>
                              <p className="text-slate-500">{client.contact_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>{client.payment_terms_days} Days</TableCell>
                          <TableCell>{formatCurrency(client.credit_limit)}</TableCell>
                          <TableCell>
                            {client.tds_applicable ? (
                              <Badge className="bg-purple-100 text-purple-700 cursor-pointer" onClick={() => openTdsConfigDialog(client)}>
                                {client.tds_rate}% ({client.tds_section || '194C'})
                              </Badge>
                            ) : (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-xs text-slate-500 hover:text-purple-600"
                                onClick={() => openTdsConfigDialog(client)}
                              >
                                + Configure TDS
                              </Button>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openTdsConfigDialog(client)} title="TDS Settings" className="text-purple-500 hover:text-purple-700 hover:bg-purple-50">
                                <Calculator size={16} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => editClient(client)} title="Edit">
                                <Edit2 size={16} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteClient(client)} title="Delete" className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {clients.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                            No clients found. Add your first client to get started.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* GST Consolidated View */}
            {clientsViewMode === 'gst' && (
              <div className="space-y-4">
                {gstConsolidatedData.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-slate-500">
                      No GST data available
                    </CardContent>
                  </Card>
                ) : (
                  gstConsolidatedData
                    .filter(group => !searchQuery || 
                      group.gst_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      group.clients.some(c => c.name?.toLowerCase().includes(searchQuery.toLowerCase()))
                    )
                    .map((group, idx) => (
                    <Card key={idx} className="overflow-hidden">
                      <div
                        className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100"
                        onClick={() => setExpandedGstGroups(prev => ({ ...prev, [group.gst_number]: !prev[group.gst_number] }))}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`transition-transform ${expandedGstGroups[group.gst_number] ? 'rotate-90' : ''}`}>
                            <ChevronRight size={20} />
                          </div>
                          <div>
                            <p className="font-mono font-semibold text-lg">
                              {group.gst_number === 'NO_GST' ? 'No GST Registered' : group.gst_number}
                            </p>
                            <p className="text-sm text-slate-500">{group.client_count} client(s)</p>
                          </div>
                        </div>
                        <div className="flex gap-6 text-sm">
                          <div className="text-center">
                            <p className="text-slate-500">Total Invoiced</p>
                            <p className="font-semibold text-slate-700">{formatCurrency(group.summary.total_invoiced)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-slate-500">Total Paid</p>
                            <p className="font-semibold text-green-600">{formatCurrency(group.summary.total_paid)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-slate-500">Outstanding</p>
                            <p className="font-semibold text-red-600">{formatCurrency(group.summary.outstanding)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-slate-500">Pending Inv.</p>
                            <Badge variant={group.summary.pending_invoices > 0 ? 'destructive' : 'secondary'}>
                              {group.summary.pending_invoices}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      {expandedGstGroups[group.gst_number] && (
                        <CardContent className="p-0">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-white">
                                <TableHead>Client Name</TableHead>
                                <TableHead>City/State</TableHead>
                                <TableHead>Contact</TableHead>
                                <TableHead>Credit Limit</TableHead>
                                <TableHead className="text-center">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {group.clients.map((client) => (
                                <TableRow key={client.id}>
                                  <TableCell className="font-medium">{client.name}</TableCell>
                                  <TableCell className="text-sm text-slate-500">
                                    {[client.city, client.state].filter(Boolean).join(', ') || '-'}
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm">
                                      <p>{client.contact_person || '-'}</p>
                                      <p className="text-slate-500">{client.contact_email || ''}</p>
                                    </div>
                                  </TableCell>
                                  <TableCell>{formatCurrency(client.credit_limit)}</TableCell>
                                  <TableCell className="text-center">
                                    <div className="flex justify-center gap-1">
                                      <Button variant="ghost" size="sm" onClick={() => {
                                        const fullClient = clients.find(c => c.id === client.id);
                                        if (fullClient) editClient(fullClient);
                                      }} title="Edit">
                                        <Edit2 size={16} />
                                      </Button>
                                      <Button variant="ghost" size="sm" onClick={() => {
                                        const fullClient = clients.find(c => c.id === client.id);
                                        if (fullClient) handleDeleteClient(fullClient);
                                      }} title="Delete" className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                        <Trash2 size={16} />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      )}
                    </Card>
                  ))
                )}
              </div>
            )}

            {/* Client Summary View */}
            {clientsViewMode === 'summary' && (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client Name</TableHead>
                        <TableHead>GST Number</TableHead>
                        <TableHead className="text-right">Invoices</TableHead>
                        <TableHead className="text-right">Total Invoiced</TableHead>
                        <TableHead className="text-right">Total Paid</TableHead>
                        <TableHead className="text-right">Outstanding</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clientSummaryData
                        .filter(item => !searchQuery || 
                          item.client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.client.gst_number?.toLowerCase().includes(searchQuery.toLowerCase())
                        )
                        .map((item) => (
                        <TableRow key={item.client.id}>
                          <TableCell className="font-medium">{item.client.name}</TableCell>
                          <TableCell className="font-mono text-sm">{item.client.gst_number || '-'}</TableCell>
                          <TableCell className="text-right">{item.summary.invoice_count}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(item.summary.total_invoiced)}</TableCell>
                          <TableCell className="text-right text-green-600">{formatCurrency(item.summary.total_paid)}</TableCell>
                          <TableCell className="text-right font-semibold text-red-600">{formatCurrency(item.summary.outstanding)}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-1 flex-wrap">
                              {item.summary.status_counts.pending > 0 && (
                                <Badge variant="outline" className="text-xs">P: {item.summary.status_counts.pending}</Badge>
                              )}
                              {item.summary.status_counts.overdue > 0 && (
                                <Badge variant="destructive" className="text-xs">O: {item.summary.status_counts.overdue}</Badge>
                              )}
                              {item.summary.status_counts.partially_paid > 0 && (
                                <Badge variant="secondary" className="text-xs">PP: {item.summary.status_counts.partially_paid}</Badge>
                              )}
                              {item.summary.status_counts.closed > 0 && (
                                <Badge className="text-xs bg-green-100 text-green-700">C: {item.summary.status_counts.closed}</Badge>
                              )}
                              {item.summary.invoice_count === 0 && (
                                <span className="text-slate-400 text-xs">No invoices</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => editClient(item.client)} title="Edit">
                                <Edit2 size={16} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteClient(item.client)} title="Delete" className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {clientSummaryData.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                            No client summary data available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex gap-4 items-center">
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    placeholder="Search invoices..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partially_paid">Partially Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterClient} onValueChange={setFilterClient}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-indigo-600 hover:bg-indigo-700" onClick={resetInvoiceForm}>
                    <Plus size={16} /> Create Invoice
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 py-4">
                    {/* Basic Info */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Client *</Label>
                        <Select
                          value={invoiceForm.client_id}
                          onValueChange={(v) => setInvoiceForm({ ...invoiceForm, client_id: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select client" />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Invoice Date</Label>
                        <Input
                          type="date"
                          value={invoiceForm.invoice_date}
                          onChange={(e) => {
                            const invDate = e.target.value;
                            const dueDate = invDate ? new Date(new Date(invDate).getTime() + 45 * 86400000).toISOString().split('T')[0] : '';
                            setInvoiceForm({ ...invoiceForm, invoice_date: invDate, due_date: dueDate });
                          }}
                        />
                      </div>
                      <div>
                        <Label>Due Date</Label>
                        <Input
                          type="date"
                          value={invoiceForm.due_date}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Service Type</Label>
                        <Select
                          value={invoiceForm.service_type_id}
                          onValueChange={(v) => setInvoiceForm({ ...invoiceForm, service_type_id: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {serviceTypes.map((st) => (
                              <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Project Name</Label>
                        <Input
                          value={invoiceForm.project_name}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, project_name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Input
                          value={invoiceForm.location}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, location: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* Line Items */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <Label>Line Items</Label>
                        <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                          <Plus size={14} className="mr-1" /> Add Item
                        </Button>
                      </div>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="w-[40%]">Description</TableHead>
                              <TableHead className="w-[15%]">Qty</TableHead>
                              <TableHead className="w-[20%]">Rate</TableHead>
                              <TableHead className="w-[20%]">Amount</TableHead>
                              <TableHead className="w-[5%]"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {invoiceForm.line_items.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell>
                                  <Input
                                    value={item.description}
                                    onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                                    placeholder="Item description"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    value={item.rate}
                                    onChange={(e) => updateLineItem(idx, 'rate', e.target.value)}
                                  />
                                </TableCell>
                                <TableCell className="font-medium">
                                  {formatCurrency(item.amount)}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeLineItem(idx)}
                                    disabled={invoiceForm.line_items.length === 1}
                                  >
                                    <Trash2 size={14} className="text-red-500" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    {/* GST & TDS */}
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label>GST Rate (%)</Label>
                        <Select
                          value={invoiceForm.gst_rate.toString()}
                          onValueChange={(v) => setInvoiceForm({ ...invoiceForm, gst_rate: parseFloat(v) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">0%</SelectItem>
                            <SelectItem value="5">5%</SelectItem>
                            <SelectItem value="12">12%</SelectItem>
                            <SelectItem value="18">18%</SelectItem>
                            <SelectItem value="28">28%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <input
                          type="checkbox"
                          checked={invoiceForm.is_same_state}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, is_same_state: e.target.checked })}
                          className="h-4 w-4"
                        />
                        <Label className="mb-0">Same State (CGST+SGST)</Label>
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <input
                          type="checkbox"
                          checked={invoiceForm.tds_applicable}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, tds_applicable: e.target.checked })}
                          className="h-4 w-4"
                        />
                        <Label className="mb-0">TDS Applicable</Label>
                      </div>
                      {invoiceForm.tds_applicable && (
                        <div>
                          <Label>TDS Rate (%)</Label>
                          <Select
                            value={invoiceForm.tds_rate.toString()}
                            onValueChange={(v) => setInvoiceForm({ ...invoiceForm, tds_rate: parseFloat(v) })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1%</SelectItem>
                              <SelectItem value="2">2%</SelectItem>
                              <SelectItem value="10">10%</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/* Totals */}
                    <div className="bg-slate-50 p-4 rounded-lg">
                      {(() => {
                        const totals = calculateInvoiceTotals(invoiceForm);
                        return (
                          <div className="space-y-2 text-right">
                            <div className="flex justify-end gap-8">
                              <span className="text-slate-600">Subtotal:</span>
                              <span className="font-medium w-32">{formatCurrency(totals.subtotal)}</span>
                            </div>
                            {invoiceForm.is_same_state ? (
                              <>
                                <div className="flex justify-end gap-8">
                                  <span className="text-slate-600">CGST ({invoiceForm.gst_rate/2}%):</span>
                                  <span className="w-32">{formatCurrency(totals.cgst)}</span>
                                </div>
                                <div className="flex justify-end gap-8">
                                  <span className="text-slate-600">SGST ({invoiceForm.gst_rate/2}%):</span>
                                  <span className="w-32">{formatCurrency(totals.sgst)}</span>
                                </div>
                              </>
                            ) : (
                              <div className="flex justify-end gap-8">
                                <span className="text-slate-600">IGST ({invoiceForm.gst_rate}%):</span>
                                <span className="w-32">{formatCurrency(totals.igst)}</span>
                              </div>
                            )}
                            <div className="flex justify-end gap-8 text-lg font-bold border-t pt-2">
                              <span>Total:</span>
                              <span className="w-32">{formatCurrency(totals.total)}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={invoiceForm.notes}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                        placeholder="Additional notes..."
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveInvoice} className="bg-indigo-600 hover:bg-indigo-700">
                      {editingInvoice ? 'Update' : 'Create'} Invoice
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortHeader field="invoice_number">Invoice No</SortHeader>
                      <SortHeader field="client_name">Client</SortHeader>
                      <SortHeader field="invoice_date">Date</SortHeader>
                      <SortHeader field="due_date">Due Date</SortHeader>
                      <SortHeader field="total_amount" className="text-right">Amount</SortHeader>
                      <SortHeader field="outstanding_amount" className="text-right">Due</SortHeader>
                      <SortHeader field="status">Status</SortHeader>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortData(filteredInvoices).map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-mono font-medium">{inv.invoice_number}</TableCell>
                        <TableCell>{inv.client_name}</TableCell>
                        <TableCell>{inv.invoice_date}</TableCell>
                        <TableCell>{inv.due_date}</TableCell>
                        <TableCell className="text-right">{formatCurrency(inv.total_amount)}</TableCell>
                        <TableCell className="text-right font-medium text-red-600">
                          {formatCurrency(inv.amount_due)}
                        </TableCell>
                        <TableCell>{getStatusBadge(inv.status)}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center gap-1">
                            {inv.status !== 'closed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openPaymentDialog(inv)}
                                title="Record Payment"
                              >
                                <CreditCard size={16} className="text-emerald-600" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" title="View" onClick={() => setViewingInvoice(inv)}>
                              <Eye size={16} />
                            </Button>
                            {inv.status !== 'closed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => editInvoice(inv)}
                                title="Edit Invoice"
                              >
                                <Edit2 size={16} />
                              </Button>
                            )}
                            {inv.status !== 'closed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteInvoice(inv.id, inv.invoice_number)}
                                title="Delete Invoice"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredInvoices.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          No invoices found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bulk Upload Tab */}
          <TabsContent value="bulk-upload" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="text-indigo-600" size={20} />
                  AI-Powered Invoice Upload
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Upload Section */}
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-indigo-400 transition-colors">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".pdf"
                    multiple
                    className="hidden"
                    id="bulk-invoice-upload"
                  />
                  <label htmlFor="bulk-invoice-upload" className="cursor-pointer">
                    <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Upload className="text-indigo-600" size={32} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Upload Invoice PDFs</h3>
                    <p className="text-slate-500 text-sm mb-4">
                      Drag & drop or click to select PDF invoices. AI will extract all details automatically.
                    </p>
                    <Button variant="outline" className="gap-2">
                      <FileUp size={16} /> Select Files
                    </Button>
                  </label>
                </div>

                {/* Selected Files */}
                {uploadedFiles.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-slate-900">Selected Files ({uploadedFiles.length})</h4>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={resetBulkUpload}>
                          Clear All
                        </Button>
                        <Button
                          onClick={handleExtractInvoices}
                          disabled={extracting}
                          className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                        >
                          {extracting ? (
                            <>
                              <Loader2 size={16} className="animate-spin" /> Extracting...
                            </>
                          ) : (
                            <>
                              <Sparkles size={16} /> Extract with AI
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {uploadedFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <FileText className="text-red-500" size={20} />
                          <span className="text-sm text-slate-700 truncate flex-1">{file.name}</span>
                          <span className="text-xs text-slate-400">
                            {(file.size / 1024).toFixed(0)} KB
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Extracted Invoices */}
                {extractedInvoices.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-slate-900">
                        Extracted Invoices ({extractedInvoices.filter(i => i.success).length} successful)
                      </h4>
                      <Button
                        onClick={() => handleSaveExtractedInvoices(false)}
                        disabled={savingInvoices || !extractedInvoices.some(i => i.success && i.selected)}
                        className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                      >
                        {savingInvoices ? (
                          <>
                            <Loader2 size={16} className="animate-spin" /> Saving...
                          </>
                        ) : (
                          <>
                            <Check size={16} /> Save Selected Invoices
                          </>
                        )}
                      </Button>
                    </div>

                    <div className="space-y-4">
                      {extractedInvoices.map((item, idx) => (
                        <Card key={idx} className={!item.success ? 'border-red-200 bg-red-50' : item.selected ? 'border-emerald-200' : 'opacity-60'}>
                          <CardContent className="p-4">
                            {item.success ? (
                              <div className="space-y-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      checked={item.selected}
                                      onChange={(e) => updateExtractedInvoice(idx, 'selected', e.target.checked)}
                                      className="h-5 w-5 rounded border-slate-300"
                                    />
                                    <div>
                                      <p className="font-medium text-slate-900">{item.filename}</p>
                                      <p className="text-sm text-slate-500">Invoice: {item.data.invoice_number || 'N/A'}</p>
                                    </div>
                                  </div>
                                  <Badge className="bg-emerald-100 text-emerald-700">
                                    <Check size={12} className="mr-1" /> Extracted
                                  </Badge>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div>
                                    <Label className="text-xs text-slate-500">Client Name</Label>
                                    <Input
                                      value={item.data.client_name || ''}
                                      onChange={(e) => updateExtractedInvoice(idx, 'client_name', e.target.value)}
                                      className="h-8"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-500">GST Number</Label>
                                    <Input
                                      value={item.data.client_gst || ''}
                                      onChange={(e) => updateExtractedInvoice(idx, 'client_gst', e.target.value)}
                                      className="h-8"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-500">Invoice Date</Label>
                                    <Input
                                      type="date"
                                      value={item.data.invoice_date || ''}
                                      onChange={(e) => updateExtractedInvoice(idx, 'invoice_date', e.target.value)}
                                      className="h-8"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-500">Total Amount</Label>
                                    <Input
                                      type="number"
                                      value={item.data.total_amount || ''}
                                      onChange={(e) => updateExtractedInvoice(idx, 'total_amount', parseFloat(e.target.value))}
                                      className="h-8"
                                    />
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div>
                                    <Label className="text-xs text-slate-500">Subtotal</Label>
                                    <Input
                                      type="number"
                                      value={item.data.subtotal || ''}
                                      onChange={(e) => updateExtractedInvoice(idx, 'subtotal', parseFloat(e.target.value))}
                                      className="h-8"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-500">GST Rate (%)</Label>
                                    <Input
                                      type="number"
                                      value={item.data.gst_rate || ''}
                                      onChange={(e) => updateExtractedInvoice(idx, 'gst_rate', parseFloat(e.target.value))}
                                      className="h-8"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-500">CGST</Label>
                                    <Input
                                      type="number"
                                      value={item.data.cgst || ''}
                                      onChange={(e) => updateExtractedInvoice(idx, 'cgst', parseFloat(e.target.value))}
                                      className="h-8"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-500">SGST/IGST</Label>
                                    <Input
                                      type="number"
                                      value={item.data.sgst || item.data.igst || ''}
                                      onChange={(e) => updateExtractedInvoice(idx, 'sgst', parseFloat(e.target.value))}
                                      className="h-8"
                                    />
                                  </div>
                                </div>

                                {/* Client Selection */}
                                <div className="border-t pt-4">
                                  <Label className="text-sm font-medium">Client Assignment</Label>
                                  {item.existingClient ? (
                                    <div className="mt-2 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                                      <div>
                                        <p className="text-sm font-medium text-blue-900">
                                          Matched: {item.existingClient.name}
                                        </p>
                                        <p className="text-xs text-blue-600">
                                          GST: {item.existingClient.gst_number || 'N/A'}
                                        </p>
                                      </div>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => updateExtractedInvoice(idx, 'createNewClient', true)}
                                      >
                                        Create New Instead
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="mt-2 space-y-2">
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={item.createNewClient}
                                          onChange={(e) => updateExtractedInvoice(idx, 'createNewClient', e.target.checked)}
                                          className="h-4 w-4"
                                        />
                                        <Label className="text-sm">Create new client from extracted data</Label>
                                      </div>
                                      {!item.createNewClient && (
                                        <Select
                                          value={item.client_id || ''}
                                          onValueChange={(v) => updateExtractedInvoice(idx, 'client_id', v)}
                                        >
                                          <SelectTrigger className="h-9">
                                            <SelectValue placeholder="Select existing client" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {clients.map(c => (
                                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Line Items Preview */}
                                {item.data.line_items && item.data.line_items.length > 0 && (
                                  <div className="border-t pt-4">
                                    <Label className="text-sm font-medium mb-2 block">Line Items</Label>
                                    <div className="bg-slate-50 rounded-lg p-3 text-sm">
                                      {item.data.line_items.map((lineItem, liIdx) => (
                                        <div key={liIdx} className="flex justify-between py-1 border-b last:border-0">
                                          <span className="text-slate-600">{lineItem.description || `Item ${liIdx + 1}`}</span>
                                          <span className="font-medium">{formatCurrency(lineItem.amount)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                  <X className="text-red-500" size={20} />
                                </div>
                                <div>
                                  <p className="font-medium text-red-900">{item.filename}</p>
                                  <p className="text-sm text-red-600">{item.error}</p>
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Instructions */}
                {uploadedFiles.length === 0 && extractedInvoices.length === 0 && (
                  <div className="bg-slate-50 rounded-xl p-6">
                    <h4 className="font-medium text-slate-900 mb-3">How it works:</h4>
                    <ol className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium">1</span>
                        <span>Upload one or more invoice PDFs</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium">2</span>
                        <span>AI extracts client details, amounts, GST, line items automatically</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium">3</span>
                        <span>Review and edit extracted data if needed</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium">4</span>
                        <span>Auto-match or create new clients, then save invoices</span>
                      </li>
                    </ol>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Payment History</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">TDS</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((pay) => (
                      <TableRow key={pay.id}>
                        <TableCell>{pay.payment_date}</TableCell>
                        <TableCell className="font-mono">{pay.invoice_id?.slice(0, 8)}...</TableCell>
                        <TableCell>
                          <Badge variant="outline">{pay.payment_mode}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{pay.bank_reference || '-'}</TableCell>
                        <TableCell className="text-right text-emerald-600">{formatCurrency(pay.amount)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(pay.tds_deducted)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(pay.total_adjustment)}</TableCell>
                      </TableRow>
                    ))}
                    {payments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                          No payments recorded yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aging Tab */}
          <TabsContent value="aging" className="space-y-4">
            {/* Aging Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: '0-30 Days', value: dashboardData?.aging?.['0_30'] || 0, color: 'emerald' },
                { label: '31-60 Days', value: dashboardData?.aging?.['31_60'] || 0, color: 'blue' },
                { label: '61-90 Days', value: dashboardData?.aging?.['61_90'] || 0, color: 'amber' },
                { label: '90+ Days', value: dashboardData?.aging?.['90_plus'] || 0, color: 'red' }
              ].map((bucket, idx) => (
                <Card key={idx} className={`border-${bucket.color}-200`}>
                  <CardContent className="p-4">
                    <p className={`text-sm text-${bucket.color}-600 font-medium`}>{bucket.label}</p>
                    <p className={`text-xl font-bold text-${bucket.color}-900`}>{formatCurrency(bucket.value)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Detailed Aging Report</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortHeader field="client_name">Client</SortHeader>
                      <SortHeader field="invoice_number">Invoice No</SortHeader>
                      <SortHeader field="invoice_date">Invoice Date</SortHeader>
                      <SortHeader field="due_date">Due Date</SortHeader>
                      <SortHeader field="days_old" className="text-right">Age (Days)</SortHeader>
                      <SortHeader field="amount_due" className="text-right">Amount Due</SortHeader>
                      <SortHeader field="bucket">Bucket</SortHeader>
                      <SortHeader field="status">Status</SortHeader>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortData(agingData).map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{item.client_name}</TableCell>
                        <TableCell className="font-mono">{item.invoice_number}</TableCell>
                        <TableCell>{item.invoice_date}</TableCell>
                        <TableCell>{item.due_date || '-'}</TableCell>
                        <TableCell className="text-right">
                          <span className={
                            item.days_old > 90 ? 'text-red-600 font-bold' :
                            item.days_old > 60 ? 'text-amber-600 font-medium' :
                            item.days_old > 30 ? 'text-blue-600' : ''
                          }>
                            {item.days_old}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(item.amount_due)}</TableCell>
                        <TableCell>
                          <Badge className={
                            item.bucket === '0-30' ? 'bg-emerald-100 text-emerald-700' :
                            item.bucket === '31-60' ? 'bg-blue-100 text-blue-700' :
                            item.bucket === '61-90' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }>
                            {item.bucket}
                          </Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(item.status)}</TableCell>
                      </TableRow>
                    ))}
                    {agingData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          No outstanding invoices
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            {!selectedReport ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { title: 'Outstanding Summary', icon: FileText, desc: 'Client-wise outstanding', color: 'bg-blue-100 text-blue-600' },
                  { title: 'GST Summary', icon: Receipt, desc: 'GST output report', color: 'bg-green-100 text-green-600' },
                  { title: 'TDS Reconciliation', icon: Calculator, desc: 'TDS deducted summary', color: 'bg-purple-100 text-purple-600' },
                  { title: 'Collection Report', icon: TrendingUp, desc: 'Collection efficiency', color: 'bg-orange-100 text-orange-600' }
                ].map((report, idx) => (
                  <Card 
                    key={idx} 
                    className="hover:shadow-md transition-all cursor-pointer hover:scale-[1.02] border-2 hover:border-indigo-300"
                    onClick={() => generateReport(report.title)}
                    data-testid={`report-card-${report.title.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <CardContent className="p-6 text-center">
                      <div className={`w-12 h-12 ${report.color.split(' ')[0]} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                        <report.icon className={report.color.split(' ')[1]} size={24} />
                      </div>
                      <h3 className="font-medium text-slate-900">{report.title}</h3>
                      <p className="text-sm text-slate-500 mt-1">{report.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Report Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => { setSelectedReport(null); setReportData(null); }}
                      className="gap-2"
                    >
                      <ChevronRight className="rotate-180" size={16} />
                      Back to Reports
                    </Button>
                    <h2 className="text-xl font-semibold text-slate-900">{selectedReport}</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    {(selectedReport === 'GST Summary' || selectedReport === 'Collection Report') && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="date"
                          value={reportDateRange.from}
                          onChange={(e) => setReportDateRange({ ...reportDateRange, from: e.target.value })}
                          className="w-36"
                        />
                        <span className="text-slate-500">to</span>
                        <Input
                          type="date"
                          value={reportDateRange.to}
                          onChange={(e) => setReportDateRange({ ...reportDateRange, to: e.target.value })}
                          className="w-36"
                        />
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => generateReport(selectedReport)}
                          className="gap-2"
                        >
                          <RefreshCw size={14} />
                          Refresh
                        </Button>
                      </div>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={exportReportToCsv}
                      disabled={!reportData}
                      className="gap-2"
                    >
                      <Download size={14} />
                      Export CSV
                    </Button>
                  </div>
                </div>

                {/* Report Content */}
                {reportLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="animate-spin text-indigo-600" size={32} />
                  </div>
                ) : reportData ? (
                  <div className="space-y-4">
                    {/* Outstanding Summary Report */}
                    {selectedReport === 'Outstanding Summary' && (
                      <>
                        <div className="grid grid-cols-4 gap-4">
                          <Card className="bg-blue-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-blue-700">{reportData.summary.total_clients}</p>
                              <p className="text-sm text-blue-600">Clients with Outstanding</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-amber-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-amber-700">{formatCurrency(reportData.summary.total_outstanding)}</p>
                              <p className="text-sm text-amber-600">Total Outstanding</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-red-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-red-700">{formatCurrency(reportData.summary.total_overdue)}</p>
                              <p className="text-sm text-red-600">Total Overdue</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-slate-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-slate-700">{reportData.summary.overdue_percentage}%</p>
                              <p className="text-sm text-slate-600">Overdue Percentage</p>
                            </CardContent>
                          </Card>
                        </div>
                        <Card>
                          <CardContent className="p-0">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Client Name</TableHead>
                                  <TableHead>GST Number</TableHead>
                                  <TableHead className="text-center">Invoices</TableHead>
                                  <TableHead className="text-center">Pending</TableHead>
                                  <TableHead className="text-right">Total Invoiced</TableHead>
                                  <TableHead className="text-right">Received</TableHead>
                                  <TableHead className="text-right">Outstanding</TableHead>
                                  <TableHead className="text-right">Overdue</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {reportData.details.map((row, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-medium">{row.client_name}</TableCell>
                                    <TableCell className="font-mono text-sm">{row.gst_number || '-'}</TableCell>
                                    <TableCell className="text-center">{row.invoice_count}</TableCell>
                                    <TableCell className="text-center">{row.pending_count}</TableCell>
                                    <TableCell className="text-right">{formatCurrency(row.total_invoiced)}</TableCell>
                                    <TableCell className="text-right text-green-600">{formatCurrency(row.total_received)}</TableCell>
                                    <TableCell className="text-right font-medium text-amber-600">{formatCurrency(row.total_outstanding)}</TableCell>
                                    <TableCell className="text-right text-red-600">{formatCurrency(row.overdue_amount)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </CardContent>
                        </Card>
                      </>
                    )}

                    {/* GST Summary Report */}
                    {selectedReport === 'GST Summary' && (
                      <>
                        <div className="grid grid-cols-5 gap-4">
                          <Card className="bg-blue-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-blue-700">{reportData.summary.total_invoices}</p>
                              <p className="text-sm text-blue-600">Invoices</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-slate-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-slate-700">{formatCurrency(reportData.summary.total_taxable_value)}</p>
                              <p className="text-sm text-slate-600">Taxable Value</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-green-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-green-700">{formatCurrency(reportData.summary.total_cgst)}</p>
                              <p className="text-sm text-green-600">CGST</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-green-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-green-700">{formatCurrency(reportData.summary.total_sgst)}</p>
                              <p className="text-sm text-green-600">SGST</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-purple-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-purple-700">{formatCurrency(reportData.summary.total_igst)}</p>
                              <p className="text-sm text-purple-600">IGST</p>
                            </CardContent>
                          </Card>
                        </div>
                        <Card>
                          <CardContent className="p-0">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>GST Rate</TableHead>
                                  <TableHead className="text-center">Invoice Count</TableHead>
                                  <TableHead className="text-right">Taxable Value</TableHead>
                                  <TableHead className="text-right">CGST</TableHead>
                                  <TableHead className="text-right">SGST</TableHead>
                                  <TableHead className="text-right">IGST</TableHead>
                                  <TableHead className="text-right">Total GST</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {reportData.details.map((row, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-medium">{row.rate}%</TableCell>
                                    <TableCell className="text-center">{row.invoice_count}</TableCell>
                                    <TableCell className="text-right">{formatCurrency(row.taxable_value)}</TableCell>
                                    <TableCell className="text-right text-green-600">{formatCurrency(row.cgst)}</TableCell>
                                    <TableCell className="text-right text-green-600">{formatCurrency(row.sgst)}</TableCell>
                                    <TableCell className="text-right text-purple-600">{formatCurrency(row.igst)}</TableCell>
                                    <TableCell className="text-right font-medium">{formatCurrency(row.total_gst)}</TableCell>
                                  </TableRow>
                                ))}
                                <TableRow className="bg-slate-50 font-semibold">
                                  <TableCell>Total</TableCell>
                                  <TableCell className="text-center">{reportData.summary.total_invoices}</TableCell>
                                  <TableCell className="text-right">{formatCurrency(reportData.summary.total_taxable_value)}</TableCell>
                                  <TableCell className="text-right text-green-600">{formatCurrency(reportData.summary.total_cgst)}</TableCell>
                                  <TableCell className="text-right text-green-600">{formatCurrency(reportData.summary.total_sgst)}</TableCell>
                                  <TableCell className="text-right text-purple-600">{formatCurrency(reportData.summary.total_igst)}</TableCell>
                                  <TableCell className="text-right">{formatCurrency(reportData.summary.total_gst)}</TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </CardContent>
                        </Card>
                      </>
                    )}

                    {/* TDS Reconciliation Report */}
                    {selectedReport === 'TDS Reconciliation' && (
                      <>
                        <div className="grid grid-cols-3 gap-4">
                          <Card className="bg-purple-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-purple-700">{reportData.summary.total_tds_applicable_invoices}</p>
                              <p className="text-sm text-purple-600">TDS Applicable Invoices</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-blue-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-blue-700">{reportData.summary.clients_with_tds}</p>
                              <p className="text-sm text-blue-600">Clients with TDS</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-green-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-green-700">{formatCurrency(reportData.summary.total_tds_deducted)}</p>
                              <p className="text-sm text-green-600">Total TDS Deducted</p>
                            </CardContent>
                          </Card>
                        </div>
                        <Card>
                          <CardContent className="p-0">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Client Name</TableHead>
                                  <TableHead>TAN</TableHead>
                                  <TableHead className="text-right">Total Amount</TableHead>
                                  <TableHead className="text-center">TDS Rate</TableHead>
                                  <TableHead className="text-right">TDS Deducted</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {reportData.details.length > 0 ? (
                                  reportData.details.map((row, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell className="font-medium">{row.client_name}</TableCell>
                                      <TableCell className="font-mono text-sm">{row.tan}</TableCell>
                                      <TableCell className="text-right">{formatCurrency(row.total_amount)}</TableCell>
                                      <TableCell className="text-center">{row.tds_rate}%</TableCell>
                                      <TableCell className="text-right font-medium text-green-600">{formatCurrency(row.tds_deducted)}</TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                                      No TDS deductions recorded
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </CardContent>
                        </Card>
                      </>
                    )}

                    {/* Collection Report */}
                    {selectedReport === 'Collection Report' && (
                      <>
                        <div className="grid grid-cols-4 gap-4">
                          <Card className="bg-blue-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-blue-700">{formatCurrency(reportData.summary.total_invoiced)}</p>
                              <p className="text-sm text-blue-600">Total Invoiced</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-green-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-green-700">{formatCurrency(reportData.summary.total_collected)}</p>
                              <p className="text-sm text-green-600">Total Collected</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-amber-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-amber-700">{formatCurrency(reportData.summary.total_pending)}</p>
                              <p className="text-sm text-amber-600">Pending Collection</p>
                            </CardContent>
                          </Card>
                          <Card className={`${parseFloat(reportData.summary.collection_efficiency) >= 80 ? 'bg-green-100' : parseFloat(reportData.summary.collection_efficiency) >= 50 ? 'bg-amber-100' : 'bg-red-100'}`}>
                            <CardContent className="p-4 text-center">
                              <p className={`text-2xl font-bold ${parseFloat(reportData.summary.collection_efficiency) >= 80 ? 'text-green-700' : parseFloat(reportData.summary.collection_efficiency) >= 50 ? 'text-amber-700' : 'text-red-700'}`}>
                                {reportData.summary.collection_efficiency}%
                              </p>
                              <p className="text-sm text-slate-600">Collection Efficiency</p>
                            </CardContent>
                          </Card>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          {/* Monthly Breakdown */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-lg">Monthly Breakdown</CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Month</TableHead>
                                    <TableHead className="text-right">Invoiced</TableHead>
                                    <TableHead className="text-right">Collected</TableHead>
                                    <TableHead className="text-right">Efficiency</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {reportData.monthly?.length > 0 ? (
                                    reportData.monthly.map((row, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell className="font-medium">{row.month}</TableCell>
                                        <TableCell className="text-right">{formatCurrency(row.invoiced)}</TableCell>
                                        <TableCell className="text-right text-green-600">{formatCurrency(row.collected)}</TableCell>
                                        <TableCell className="text-right">
                                          <Badge className={parseFloat(row.efficiency) >= 80 ? 'bg-green-100 text-green-700' : parseFloat(row.efficiency) >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                                            {row.efficiency}%
                                          </Badge>
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  ) : (
                                    <TableRow>
                                      <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                                        No data for selected period
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </CardContent>
                          </Card>

                          {/* Payment Mode Breakdown */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-lg">Payment Mode Breakdown</CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Payment Mode</TableHead>
                                    <TableHead className="text-center">Count</TableHead>
                                    <TableHead className="text-right">Amount</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {reportData.payment_modes?.length > 0 ? (
                                    reportData.payment_modes.map((row, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell className="font-medium">{row.mode}</TableCell>
                                        <TableCell className="text-center">{row.count}</TableCell>
                                        <TableCell className="text-right text-green-600">{formatCurrency(row.amount)}</TableCell>
                                      </TableRow>
                                    ))
                                  ) : (
                                    <TableRow>
                                      <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                                        No payments recorded
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </CardContent>
                          </Card>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <AlertCircle className="mx-auto mb-4" size={48} />
                    <p>Failed to load report data</p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Payment Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-4 py-4">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-sm text-slate-500">Invoice: <span className="font-mono font-medium text-slate-900">{selectedInvoice.invoice_number}</span></p>
                  <p className="text-sm text-slate-500">Amount Due: <span className="font-medium text-red-600">{formatCurrency(selectedInvoice.amount_due)}</span></p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Payment Date</Label>
                    <Input
                      type="date"
                      value={paymentForm.payment_date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Payment Mode</Label>
                    <Select
                      value={paymentForm.payment_mode}
                      onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_mode: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NEFT">NEFT</SelectItem>
                        <SelectItem value="RTGS">RTGS</SelectItem>
                        <SelectItem value="UPI">UPI</SelectItem>
                        <SelectItem value="Cheque">Cheque</SelectItem>
                        <SelectItem value="Cash">Cash</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Amount Received (₹)</Label>
                    <Input
                      type="number"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>TDS Deducted (₹)</Label>
                    <Input
                      type="number"
                      value={paymentForm.tds_deducted}
                      onChange={(e) => setPaymentForm({ ...paymentForm, tds_deducted: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Bank Reference No</Label>
                    <Input
                      value={paymentForm.bank_reference}
                      onChange={(e) => setPaymentForm({ ...paymentForm, bank_reference: e.target.value })}
                      placeholder="UTR/Transaction reference"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSavePayment} className="bg-emerald-600 hover:bg-emerald-700">
                Record Payment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* TDS Configuration Dialog */}
        <Dialog open={tdsConfigDialogOpen} onOpenChange={setTdsConfigDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calculator className="text-purple-600" size={20} />
                TDS Configuration
              </DialogTitle>
            </DialogHeader>
            {selectedClientForTds && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-900">{selectedClientForTds.name}</p>
                  <p className="text-sm text-slate-500">GST: {selectedClientForTds.gst_number || 'Not specified'}</p>
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label className="font-medium">TDS Applicable</Label>
                    <p className="text-sm text-slate-500">Enable TDS deduction for this client</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={tdsConfigForm.tds_applicable}
                    onChange={(e) => setTdsConfigForm({ ...tdsConfigForm, tds_applicable: e.target.checked })}
                    className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                  />
                </div>

                {tdsConfigForm.tds_applicable && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>TDS Rate (%)</Label>
                        <Select
                          value={tdsConfigForm.tds_rate}
                          onValueChange={(v) => setTdsConfigForm({ ...tdsConfigForm, tds_rate: v })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1%</SelectItem>
                            <SelectItem value="2">2%</SelectItem>
                            <SelectItem value="5">5%</SelectItem>
                            <SelectItem value="10">10%</SelectItem>
                            <SelectItem value="20">20%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>TDS Section</Label>
                        <Select
                          value={tdsConfigForm.tds_section}
                          onValueChange={(v) => setTdsConfigForm({ ...tdsConfigForm, tds_section: v })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="194C">194C - Contract</SelectItem>
                            <SelectItem value="194J">194J - Professional</SelectItem>
                            <SelectItem value="194H">194H - Commission</SelectItem>
                            <SelectItem value="194I">194I - Rent</SelectItem>
                            <SelectItem value="194A">194A - Interest</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label>TAN Number</Label>
                      <Input
                        value={tdsConfigForm.tan}
                        onChange={(e) => setTdsConfigForm({ ...tdsConfigForm, tan: e.target.value.toUpperCase() })}
                        placeholder="Enter client's TAN (e.g., BLRI12345F)"
                        className="mt-1 font-mono uppercase"
                        maxLength={10}
                      />
                      <p className="text-xs text-slate-500 mt-1">Tax Deduction Account Number</p>
                    </div>

                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <p className="text-sm text-purple-700">
                        <strong>Note:</strong> When TDS is enabled, invoices for this client will automatically have TDS applicable. 
                        The TDS amount will be calculated at {tdsConfigForm.tds_rate}% under section {tdsConfigForm.tds_section}.
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setTdsConfigDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveTdsConfig} className="bg-purple-600 hover:bg-purple-700 gap-2">
                <Check size={16} />
                Save TDS Configuration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Invoice Dialog */}
        <Dialog open={!!viewingInvoice} onOpenChange={(open) => !open && setViewingInvoice(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Invoice #{viewingInvoice?.invoice_number}</DialogTitle>
            </DialogHeader>
            {viewingInvoice && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div><p className="text-xs text-slate-500">Client</p><p className="font-medium">{viewingInvoice.client_name}</p></div>
                    <div><p className="text-xs text-slate-500">Invoice Date</p><p className="font-medium">{viewingInvoice.invoice_date}</p></div>
                    <div><p className="text-xs text-slate-500">Due Date</p><p className="font-medium">{viewingInvoice.due_date}</p></div>
                    <div><p className="text-xs text-slate-500">PO Number</p><p className="font-medium">{viewingInvoice.po_number || '-'}</p></div>
                  </div>
                  <div className="space-y-3">
                    <div><p className="text-xs text-slate-500">Status</p><Badge variant="outline">{viewingInvoice.status}</Badge></div>
                    <div><p className="text-xs text-slate-500">Total Amount</p><p className="font-bold text-lg">{formatCurrency(viewingInvoice.total_amount || viewingInvoice.amount)}</p></div>
                    <div><p className="text-xs text-slate-500">Amount Received</p><p className="font-medium text-emerald-600">{formatCurrency(viewingInvoice.amount_received || 0)}</p></div>
                    <div><p className="text-xs text-slate-500">Amount Due</p><p className="font-bold text-red-600">{formatCurrency(viewingInvoice.amount_due || 0)}</p></div>
                  </div>
                </div>

                {/* Line Items */}
                {viewingInvoice.line_items?.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-2">Line Items</p>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-center">HSN/SAC</TableHead>
                          <TableHead className="text-center">Qty</TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {viewingInvoice.line_items.map((item, i) => (
                          <TableRow key={i}>
                            <TableCell>{item.description || item.particular || '-'}</TableCell>
                            <TableCell className="text-center">{item.hsn_sac || '-'}</TableCell>
                            <TableCell className="text-center">{item.quantity || item.qty || '-'}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.rate || item.unit_price || 0)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(item.amount || item.total || 0)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Tax Breakdown */}
                <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{formatCurrency(viewingInvoice.subtotal || viewingInvoice.taxable_amount || 0)}</span></div>
                  {(viewingInvoice.cgst_amount > 0 || viewingInvoice.sgst_amount > 0) && (
                    <>
                      <div className="flex justify-between"><span className="text-slate-500">CGST</span><span>{formatCurrency(viewingInvoice.cgst_amount || 0)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">SGST</span><span>{formatCurrency(viewingInvoice.sgst_amount || 0)}</span></div>
                    </>
                  )}
                  {viewingInvoice.igst_amount > 0 && (
                    <div className="flex justify-between"><span className="text-slate-500">IGST</span><span>{formatCurrency(viewingInvoice.igst_amount || 0)}</span></div>
                  )}
                  {viewingInvoice.tds_amount > 0 && (
                    <div className="flex justify-between"><span className="text-slate-500">TDS Deducted</span><span className="text-red-600">-{formatCurrency(viewingInvoice.tds_amount || 0)}</span></div>
                  )}
                  <div className="flex justify-between font-bold border-t pt-1 mt-1"><span>Total</span><span>{formatCurrency(viewingInvoice.total_amount || viewingInvoice.amount || 0)}</span></div>
                </div>

                {viewingInvoice.notes && (
                  <div><p className="text-xs text-slate-500">Notes</p><p className="text-sm">{viewingInvoice.notes}</p></div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AccountsReceivable;
